import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  ButtonGroup,
  Tooltip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

/**
 * Processing Configuration Admin Component
 * Simple toggle between batch processing and event-based processing
 */
const ProcessingConfig = ({ onConfigChange }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, mode: null });

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/processing-config');
      const data = await response.json();

      if (data.success) {
        setConfig(data.data);
      } else {
        console.error('Failed to load configuration:', data.error);
        alert(`Failed to load configuration: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      alert(`Error loading configuration: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleModeToggle = (useEventBased) => {
    const mode = useEventBased ? 'event-based' : 'batch';
    const message = useEventBased
      ? 'Switch to Event-Based Processing? This will enable real-time webhook processing and disable scheduled batch imports.'
      : 'Switch to Batch Processing? This will enable scheduled daily imports and disable webhook processing.';

    setConfirmDialog({
      open: true,
      action: 'mode-change',
      mode,
      message
    });
  };

  const executeConfirmedAction = async () => {
    const { mode } = confirmDialog;
    setSwitching(true);

    try {
      const response = await fetch('/api/processing-config/mode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode, userId: 'admin' })
      });

      const result = await response.json();

      if (result.success) {
        await loadConfiguration();
        // Notify parent component about config change
        if (onConfigChange) {
          onConfigChange();
        }
        // Success notification will be shown by the UI state change
      } else {
        console.error('Mode change failed:', result.error);
        alert(`Failed to switch mode: ${result.error}`);
      }
    } catch (error) {
      console.error('Error switching processing mode:', error);
      alert(`Error switching mode: ${error.message}`);
    } finally {
      setSwitching(false);
      setConfirmDialog({ open: false, action: null, mode: null });
    }
  };


  if (loading && !config) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading processing configuration...</Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load processing configuration</Alert>
      </Box>
    );
  }

  const isEventBased = config.webhookEnabled && !config.batchEnabled;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <SettingsIcon />
        ADO Processing Configuration
      </Typography>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Choose how ADO work items are processed in your application
      </Typography>

      {/* Toggle at Top */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, mb: 3 }}>
            <Typography
              variant="h6"
              sx={{
                color: !isEventBased ? 'primary.main' : 'text.secondary',
                fontWeight: !isEventBased ? 'bold' : 'normal'
              }}
            >
              Batch Processing
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={isEventBased}
                  onChange={(e) => handleModeToggle(e.target.checked)}
                  disabled={switching}
                  size="large"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'success.main',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'success.main',
                    },
                  }}
                />
              }
              label=""
            />

            <Typography
              variant="h6"
              sx={{
                color: isEventBased ? 'success.main' : 'text.secondary',
                fontWeight: isEventBased ? 'bold' : 'normal'
              }}
            >
              Event-Based Processing
            </Typography>
          </Box>

          {/* Processing Type Descriptions */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  border: !isEventBased ? '2px solid' : '1px solid',
                  borderColor: !isEventBased ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  backgroundColor: !isEventBased ? 'primary.50' : 'grey.50',
                  transition: 'all 0.3s ease'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <RefreshIcon color={!isEventBased ? 'primary' : 'disabled'} sx={{ mr: 1 }} />
                  <Typography variant="h6" color={!isEventBased ? 'primary.main' : 'text.secondary'}>
                    Batch Processing
                  </Typography>
                  {!isEventBased && (
                    <Chip label="ACTIVE" color="primary" size="small" sx={{ ml: 'auto' }} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • Scheduled daily imports from ADO<br/>
                  • Reliable and predictable data sync<br/>
                  • Suitable for stable workflows<br/>
                  • Work Items and Tests import tabs available
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 3,
                  border: isEventBased ? '2px solid' : '1px solid',
                  borderColor: isEventBased ? 'success.main' : 'divider',
                  borderRadius: 2,
                  backgroundColor: isEventBased ? 'success.50' : 'grey.50',
                  transition: 'all 0.3s ease'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircleIcon color={isEventBased ? 'success' : 'disabled'} sx={{ mr: 1 }} />
                  <Typography variant="h6" color={isEventBased ? 'success.main' : 'text.secondary'}>
                    Event-Based Processing
                  </Typography>
                  {isEventBased && (
                    <Chip label="ACTIVE" color="success" size="small" sx={{ ml: 'auto' }} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  • Real-time webhook processing<br/>
                  • Immediate updates when ADO changes<br/>
                  • Advanced real-time data sync<br/>
                  • Work Items and Tests tabs disabled
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Current Status - Simplified */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Current Status:
          </Typography>
          <Chip
            label={isEventBased ? 'Event-Based Processing Active' : 'Batch Processing Active'}
            color={isEventBased ? 'success' : 'primary'}
            size="small"
          />
        </Box>

        <Button
          variant="text"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadConfiguration}
          disabled={loading || switching}
        >
          Refresh
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: null, mode: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Processing Mode Change</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will change how ADO work items are processed in the application.
          </Alert>
          <Typography>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, action: null, mode: null })}
            disabled={switching}
          >
            Cancel
          </Button>
          <Button
            onClick={executeConfirmedAction}
            variant="contained"
            disabled={switching}
          >
            {switching ? 'Switching...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {(loading || switching) && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />
      )}
    </Box>
  );
};

export default ProcessingConfig;